import { useMemo, useState } from "react";
import { X, Upload, ClipboardPaste, Download, AlertTriangle, Check } from "lucide-react";
import { api } from "../lib/apiClient";

/**
 * Bulk-import Pre-Match Tasks via CSV file upload or paste-from-Excel.
 * Zero third-party dependency — the parser handles both `,` and `\t` delimiters
 * and typical quoted-field cases from the sample template.
 */

const REQUIRED_COLS = ["department", "title"];
const OPTIONAL_COLS = ["description", "due_date", "priority"];

function parseRows(text) {
  if (!text) return { headers: [], rows: [] };
  const clean = text.replace(/^\ufeff/, "").replace(/^sep=,\r?\n/, "").trim();
  if (!clean) return { headers: [], rows: [] };
  // Detect delimiter — TAB (paste from Excel) or comma (CSV file)
  const firstLine = clean.split(/\r?\n/)[0];
  const delim = firstLine.includes("\t") ? "\t" : ",";

  const parseLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQ) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') { inQ = false; }
        else { cur += ch; }
      } else {
        if (ch === '"') { inQ = true; }
        else if (ch === delim) { out.push(cur); cur = ""; }
        else { cur += ch; }
      }
    }
    out.push(cur);
    return out;
  };

  const lines = clean.split(/\r?\n/).filter((l) => l.trim().length);
  const headers = parseLine(lines[0]).map((h) => h.trim().toLowerCase());
  const rows = lines.slice(1).map((l) => {
    const cells = parseLine(l);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (cells[i] ?? "").trim(); });
    return obj;
  });
  return { headers, rows };
}

export default function BulkTasksModal({ open, onClose, onImported, matchId }) {
  const [mode, setMode] = useState("upload"); // "upload" | "paste"
  const [pasteText, setPasteText] = useState("");
  const [autoCreate, setAutoCreate] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parsed = useMemo(() => parseRows(pasteText), [pasteText]);
  const validation = useMemo(() => {
    if (!parsed.rows.length) return { valid: [], invalid: [] };
    const valid = []; const invalid = [];
    parsed.rows.forEach((r, i) => {
      const missing = REQUIRED_COLS.filter((c) => !r[c] || !String(r[c]).trim());
      if (missing.length) invalid.push({ row: i + 1, reason: `missing: ${missing.join(", ")}`, data: r });
      else valid.push({ row: i + 1, data: r });
    });
    return { valid, invalid };
  }, [parsed]);

  if (!open) return null;

  const handleFile = async (file) => {
    const text = await file.text();
    setPasteText(text);
    setMode("paste"); // switch view to show parsed preview
  };

  const downloadSample = async () => {
    try {
      const res = await api.get(`/reference/pre-match-tasks/sample.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "pre-match-tasks-sample.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Could not download the sample file.");
    }
  };

  const doImport = async () => {
    if (!validation.valid.length) return;
    setBusy(true); setResult(null);
    try {
      const rows = validation.valid.map((v) => v.data);
      const { data } = await api.post(`/matches/${matchId}/pre-match-tasks/bulk`, {
        rows,
        auto_create_departments: autoCreate,
      });
      setResult(data);
      if (data.created > 0 && onImported) onImported();
    } catch (e) {
      alert(e.response?.data?.detail || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="bulk-tasks-modal">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-obsidian border border-cmdBorder w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-cmdBorder flex items-center justify-between">
          <div>
            <div className="mc-label">Bulk Import</div>
            <div className="font-head text-2xl font-black uppercase tracking-tight">Pre-Match Tasks</div>
          </div>
          <button onClick={onClose} className="border border-cmdBorder hover:border-white h-9 w-9 flex items-center justify-center" data-testid="bulk-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={downloadSample} className="flex items-center gap-2 border border-cmdBorder hover:border-white px-3 h-10 text-xs uppercase tracking-widest" data-testid="bulk-download-sample">
              <Download className="w-4 h-4" /> Download Sample CSV
            </button>
            <label className="flex items-center gap-2 border border-cmdBorder hover:border-white px-3 h-10 text-xs uppercase tracking-widest cursor-pointer" data-testid="bulk-upload-file">
              <Upload className="w-4 h-4" /> Upload CSV
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
            </label>
            <button onClick={() => setMode("paste")} className={`flex items-center gap-2 border px-3 h-10 text-xs uppercase tracking-widest ${mode === "paste" ? "border-white bg-white/5" : "border-cmdBorder hover:border-white"}`} data-testid="bulk-paste-toggle">
              <ClipboardPaste className="w-4 h-4" /> Paste From Excel
            </button>
          </div>

          <div className="text-xs text-zinc-500">
            Required columns: <span className="text-white">department, title</span>. Optional:{" "}
            <span className="text-white">description, due_date (YYYY-MM-DD), priority (critical / high / medium / low)</span>.
            The parser accepts both comma-separated (CSV) and tab-separated (paste from Excel / Sheets) rows.
          </div>

          {mode === "paste" && (
            <textarea
              className="w-full h-40 bg-black border border-cmdBorder p-3 text-xs font-mono-live"
              placeholder="Paste rows here — first row must be the header. Example:&#10;department,title,description,due_date,priority&#10;Security,Perimeter check,,2026-02-14,high"
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              data-testid="bulk-paste-textarea"
            />
          )}

          {parsed.rows.length > 0 && (
            <div className="border border-cmdBorder">
              <div className="px-3 py-2 bg-white/[0.03] flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-zinc-400">
                  Preview · <span className="text-readyGreen">{validation.valid.length} valid</span>
                  {validation.invalid.length > 0 && <span className="text-warnAmber"> · {validation.invalid.length} to skip</span>}
                </div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                  <input type="checkbox" checked={autoCreate} onChange={(e) => setAutoCreate(e.target.checked)} data-testid="bulk-auto-create" />
                  Auto-create unknown departments
                </label>
              </div>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-black">
                    <tr className="text-left text-zinc-500 uppercase tracking-widest text-[10px]">
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">Dept</th>
                      <th className="px-3 py-2">Title</th>
                      <th className="px-3 py-2">Due</th>
                      <th className="px-3 py-2">Priority</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validation.valid.map((v) => (
                      <tr key={`v-${v.row}`} className="border-t border-cmdBorder">
                        <td className="px-3 py-2 text-zinc-500 font-mono-live">{v.row}</td>
                        <td className="px-3 py-2">{v.data.department}</td>
                        <td className="px-3 py-2 text-white">{v.data.title}</td>
                        <td className="px-3 py-2 font-mono-live text-zinc-400">{v.data.due_date || "—"}</td>
                        <td className="px-3 py-2 uppercase text-zinc-300">{v.data.priority || "medium"}</td>
                        <td className="px-3 py-2 text-readyGreen"><Check className="w-3 h-3 inline" /></td>
                      </tr>
                    ))}
                    {validation.invalid.map((v) => (
                      <tr key={`i-${v.row}`} className="border-t border-cmdBorder bg-incidentRed/10">
                        <td className="px-3 py-2 text-zinc-500 font-mono-live">{v.row}</td>
                        <td className="px-3 py-2">{v.data.department || <span className="text-zinc-600">—</span>}</td>
                        <td className="px-3 py-2 text-white">{v.data.title || <span className="text-zinc-600">—</span>}</td>
                        <td className="px-3 py-2 font-mono-live text-zinc-400">{v.data.due_date || "—"}</td>
                        <td className="px-3 py-2 uppercase text-zinc-300">{v.data.priority || "medium"}</td>
                        <td className="px-3 py-2 text-incidentRed text-[10px] uppercase" title={v.reason}>
                          <AlertTriangle className="w-3 h-3 inline mr-1" />{v.reason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div className="border border-readyGreen bg-readyGreen/10 p-3 text-sm" data-testid="bulk-result">
              <div className="text-readyGreen font-bold">Imported {result.created} task{result.created === 1 ? "" : "s"}.</div>
              {result.added_departments?.length > 0 && (
                <div className="text-zinc-300 mt-1 text-xs">New departments created: {result.added_departments.join(", ")}</div>
              )}
              {result.errors?.length > 0 && (
                <div className="text-warnAmber mt-2 text-xs">
                  <div className="font-bold">Skipped rows:</div>
                  <ul className="list-disc list-inside">
                    {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.reason}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-cmdBorder flex items-center gap-2 justify-end">
          <button onClick={onClose} className="border border-cmdBorder hover:border-white px-4 h-10 text-xs uppercase tracking-widest" data-testid="bulk-cancel">Close</button>
          <button
            onClick={doImport}
            disabled={busy || validation.valid.length === 0}
            className="bg-readyGreen text-white px-4 h-10 text-xs uppercase tracking-widest font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="bulk-import"
          >
            {busy ? "Importing…" : `Import ${validation.valid.length} Row${validation.valid.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
